import { useState } from 'react'
import {
  useDeleteIntranetDocument,
  useIntranetDocumentReads,
  useIntranetDocuments,
  useIntranetGroups,
  useMarkIntranetDocumentRead,
  useSaveIntranetDocument,
  useUploadIntranetDocumentFile
} from '../api/queries'
import type { IntranetDocument } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { formatDate } from '../lib/format'
import { fetchBlob } from '../api/client'
import { Card, EmptyState } from './ui'

/**
 * Personalehåndbog og dokumenter (docs/DECISIONS.md beslutning 104).
 *
 * <b>Filen ligger i SharePoint</b> (beslutning 023) og hentes igennem API-et, så
 * ingen skal deles ind i biblioteket. Et dokument kan også bare være et link.
 */
export function DocumentsTab() {
  const { isIntranetAdmin: isAdmin } = useAuth()
  const { data } = useIntranetDocuments(isAdmin)
  const [editing, setEditing] = useState<IntranetDocument | null | undefined>(undefined)

  // Hylderne, som kontoret selv har skrevet dem. Uden hylde staar nederst.
  const shelves = Array.from(new Set((data ?? []).map((d) => d.category ?? ''))).sort((a, b) =>
    a === '' ? 1 : b === '' ? -1 : a.localeCompare(b, 'da-DK')
  )

  return (
    <div className="stack" style={{ gap: 16 }}>
      {isAdmin && editing === undefined && (
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn--primary" onClick={() => setEditing(null)}>
            Nyt dokument
          </button>
        </div>
      )}

      {editing !== undefined && <DocumentForm document={editing} onClose={() => setEditing(undefined)} />}

      {data?.length === 0 && (
        <EmptyState>
          Der ligger ingen dokumenter endnu.
          {isAdmin ? ' Tryk "Nyt dokument" og læg personalehåndbogen op.' : ''}
        </EmptyState>
      )}

      {shelves.map((shelf) => (
        <Card key={shelf} title={shelf || 'Andet'}>
          <div className="stack" style={{ gap: 2 }}>
            {(data ?? [])
              .filter((d) => (d.category ?? '') === shelf)
              .map((document) => (
                <DocumentRow
                  key={document.id}
                  document={document}
                  onEdit={isAdmin ? () => setEditing(document) : undefined}
                />
              ))}
          </div>
        </Card>
      ))}
    </div>
  )
}

/** Én linje: navn, hvad det er, og knappen der åbner det. */
function DocumentRow({ document, onEdit }: { document: IntranetDocument; onEdit?: () => void }) {
  const { isIntranetAdmin: isAdmin } = useAuth()
  const markRead = useMarkIntranetDocumentRead()
  const [showReads, setShowReads] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function open() {
    setError(null)

    // Et link aabnes direkte; en fil hentes bag login og gemmes.
    if (document.url) {
      window.open(document.url, '_blank', 'noreferrer')
      return
    }

    if (!document.fileUrl) return

    setBusy(true)
    try {
      // Filen ligger bag login, saa den hentes som blob og aabnes derfra -
      // et <a href> ville ramme API-et uden Authorization-header.
      const blob = await fetchBlob(document.fileUrl)
      if (!blob) throw new Error('Filen kunne ikke hentes.')

      const objectUrl = URL.createObjectURL(blob)
      const link = window.document.createElement('a')
      link.href = objectUrl
      link.download = document.fileName ?? `${document.title}.pdf`
      link.click()

      // Browseren skal naa at starte hentningen, foer den frigives.
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Filen kunne ikke hentes.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="row row--between device-row" style={{ gap: 10, alignItems: 'flex-start' }}>
      <div style={{ minWidth: 0 }}>
        <div className="small">
          {document.url ? '🔗 ' : document.fileUrl ? '📄 ' : '📋 '}
          <strong>{document.title}</strong>
          {!document.isActive && <span className="tiny muted"> · slukket</span>}
        </div>
        {document.description && <div className="tiny muted">{document.description}</div>}
        <div className="tiny muted">
          {document.groupName && <>👥 {document.groupName} · </>}
          {document.fileName ?? (document.url ? 'Link' : 'Ingen fil lagt op endnu')}
          {document.sizeBytes ? ` · ${Math.max(1, Math.round(document.sizeBytes / 1024))} KB` : ''}
          {' · opdateret '}
          {formatDate(document.updatedAt)}
          {document.updatedBy && ` af ${document.updatedBy.displayName}`}
        </div>

        {document.requiresAck && (
          <div className="row" style={{ gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            {document.hasRead ? (
              <span className="tiny muted">✓ Du har kvitteret for at have læst det.</span>
            ) : (
              <button
                className="btn btn--sm btn--primary"
                disabled={markRead.isPending}
                onClick={() => void markRead.mutateAsync(document.id)}
              >
                Jeg har læst det
              </button>
            )}

            {isAdmin && (
              <button className="btn btn--sm" onClick={() => setShowReads((v) => !v)}>
                {document.readCount} af {document.recipientCount} har læst
              </button>
            )}
          </div>
        )}

        {showReads && <DocumentReads documentId={document.id} />}
        {error && <div className="error-box" style={{ marginTop: 6 }}>{error}</div>}
      </div>

      <div className="row" style={{ gap: 6, flexShrink: 0 }}>
        {/* Uden fil og uden link er der ikke noget at aabne - saa skal knappen
            heller ikke staa og se ud, som om der er. */}
        {(document.url || document.fileUrl) && (
          <button className="btn btn--sm btn--primary" disabled={busy} onClick={() => void open()}>
            {busy ? 'Henter ...' : document.url ? 'Åbn' : 'Hent'}
          </button>
        )}
        {onEdit && (
          <button className="btn btn--sm" onClick={onEdit}>
            Ret
          </button>
        )}
      </div>
    </div>
  )
}

/** Hvem har kvitteret, og hvem mangler. Kun kontoret ser den. */
function DocumentReads({ documentId }: { documentId: number }) {
  const { data } = useIntranetDocumentReads(documentId)

  if (!data) return <div className="tiny muted" style={{ marginTop: 6 }}>Henter ...</div>

  return (
    <div className="stack" style={{ gap: 4, marginTop: 8 }}>
      <div className="tiny muted">
        <strong>Har læst:</strong> {data.read.map((r) => r.user.displayName).join(', ') || 'ingen endnu'}
      </div>
      <div className="tiny warn">
        <strong>Mangler:</strong> {data.missing.map((u) => u.displayName).join(', ') || 'ingen'}
      </div>
    </div>
  )
}

/**
 * Læg et dokument op eller ret det. Kun kontoret.
 *
 * <b>Dokumentet først, filen bagefter</b> - filen skal hænge på noget, og den får
 * dokumentets nummer og titel med i navnet ude i SharePoint.
 */
function DocumentForm({ document, onClose }: { document: IntranetDocument | null; onClose: () => void }) {
  const save = useSaveIntranetDocument()
  const upload = useUploadIntranetDocumentFile()
  const remove = useDeleteIntranetDocument()
  const { data: groups } = useIntranetGroups()
  const { data: all } = useIntranetDocuments(true)

  const [title, setTitle] = useState(document?.title ?? '')
  const [description, setDescription] = useState(document?.description ?? '')
  const [category, setCategory] = useState(document?.category ?? '')
  const [groupId, setGroupId] = useState(document?.groupId ? String(document.groupId) : '')
  const [url, setUrl] = useState(document?.url ?? '')
  const [requiresAck, setRequiresAck] = useState(document?.requiresAck ?? false)
  const [sortOrder, setSortOrder] = useState(String(document?.sortOrder ?? 0))
  const [isActive, setIsActive] = useState(document?.isActive ?? true)
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Hylderne, der allerede er i brug - saa det samme kommer til at hedde det samme.
  const shelves = Array.from(new Set((all ?? []).map((d) => d.category).filter(Boolean))) as string[]

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)

    try {
      const saved = await save.mutateAsync({
        id: document?.id,
        input: {
          title: title.trim(),
          description: description.trim() || null,
          category: category.trim() || null,
          groupId: groupId ? Number(groupId) : null,
          url: file ? null : url.trim() || null,
          requiresAck,
          sortOrder: Number(sortOrder) || 0,
          isActive
        }
      })

      if (file) {
        await upload.mutateAsync({ id: saved.id, file })
      }

      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Dokumentet kunne ikke gemmes.')
    }
  }

  const busy = save.isPending || upload.isPending

  return (
    <Card title={document ? 'Ret dokumentet' : 'Nyt dokument'}>
      <form className="stack" onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Navn *</span>
            <input required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>

          <label className="field">
            <span className="field__label">Hylde</span>
            <input
              list="intranet-shelves"
              maxLength={80}
              placeholder="F.eks. Personalehåndbog"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
            <datalist id="intranet-shelves">
              {shelves.map((shelf) => (
                <option key={shelf} value={shelf} />
              ))}
            </datalist>
          </label>

          <label className="field">
            <span className="field__label">Hvem er det til?</span>
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">Hele huset</option>
              {(groups ?? []).map((group) => (
                <option key={group.id} value={group.id}>
                  {group.icon ? `${group.icon} ` : ''}
                  {group.name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Rækkefølge</span>
            <input inputMode="numeric" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
          </label>
        </div>

        <label className="field">
          <span className="field__label">Hvad er det?</span>
          <input maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">Fil {document?.fileName && <span className="tiny muted">(nu: {document.fileName})</span>}</span>
          <input
            type="file"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null)
              if (e.target.files?.[0]) setUrl('')
            }}
          />
          <span className="tiny muted">
            Filen lægges i SharePoint under Intranet/Dokumenter. Den gamle version bliver liggende dér.
          </span>
        </label>

        <label className="field">
          <span className="field__label">... eller et link</span>
          <input
            placeholder="https://..."
            maxLength={500}
            disabled={Boolean(file) || Boolean(document?.fileName)}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </label>

        <label className="row small" style={{ gap: 8 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={requiresAck}
            onChange={(e) => setRequiresAck(e.target.checked)}
          />
          Alle skal kvittere for at have læst det
        </label>

        <label className="row small" style={{ gap: 8 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          Vises for alle
        </label>

        {error && <div className="error-box">{error}</div>}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={busy}>
            {busy ? 'Gemmer ...' : 'Gem'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Fortryd
          </button>
          {document && (
            <button
              className="btn"
              type="button"
              onClick={async () => {
                if (!window.confirm(`Fjern "${document.title}"? Filen havner i SharePoints papirkurv.`)) return
                await remove.mutateAsync(document.id)
                onClose()
              }}
            >
              Fjern
            </button>
          )}
        </div>
      </form>
    </Card>
  )
}
